import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Jp } from '@/lib/jaText';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { HomeWorkflowSection } from '@/components/marketing/StrategicPositioning';
import JsonLd from '@/components/seo/JsonLd';
import NBCOlympicsBadge from '@/components/NBCOlympicsBadge';
import FounderCounterBadge from '@/components/FounderCounterBadge';
// ── Below-the-fold marketing sections are code-split via React.lazy
// (Iter 295). Each of these bundles is 30–80 KB pre-gzip and runs
// significant render work on mount (auto-rotating slideshows,
// simulated pipeline animations, federated testimonials fetch). By
// deferring them to a separate chunk we cut the initial JS payload
// and first paint by a meaningful margin without changing what the
// visitor eventually sees. Suspense fallbacks are tiny placeholder
// strips so the layout never collapses while a chunk is in-flight.
const AIShowcaseSlideshow    = lazy(() => import('@/components/marketing/AIShowcaseSlideshow'));
const RealTestimonialsStrip  = lazy(() => import('@/components/marketing/RealTestimonialsStrip'));
const LaunchProgramStrip     = lazy(() => import('@/components/marketing/LaunchProgramStrip'));
import StickyEarlyAccessSticker from '@/components/marketing/StickyEarlyAccessSticker';
import HeroIncomingMail from '@/components/home/HeroIncomingMail';
import HeroFloatingMail from '@/components/home/HeroFloatingMail';
import TollTripStatusChecker from '@/components/TollTripStatusChecker';
import EarlyAccessCTA from '@/components/EarlyAccessCTA';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import {
  Shield, ShieldCheck, Clock, FileText, Mail, ArrowRight, Check, CreditCard, Link2,
  ChevronRight, HelpCircle, Users, BarChart3, Zap, Calendar, Lock,
  Building, Truck, Receipt, Star, Globe, Download,
  Bell, Workflow, Eye, Sparkles, BookOpen,
} from 'lucide-react';

/* Brand color palette:
   Purple: #9200C7 (headings)
   Green:  #62BB46 (primary CTA, checks)
   Blue:   #0083D3 (accents)
   Orange: #FF6B00 (highlights)
   Dark:   #1a1a1a (footer)
*/

const T = {
  ja: {
    heroTag: 'ETCカード事業の実績をもとにした新サービス',
    heroTitle: 'あなたのETC利用明細管理サービス',
    heroSub: 'ETC利用明細レポートをメールで自動配信。先着100,000名様に1年間のプレミアム無料トライアル。クレジットカード登録不要。日次・週次・月次レポートをすべてお試しいただけます。',
    heroCta: '無料の週次レポートを始める',
    heroCta2: '料金はこちら',
    heroLabels: ['1年間プレミアム無料', 'クレジットカード登録不要', 'メール言語カスタマイズ', '法人・個人利用', '最大15ヶ月の履歴'],
    trust: '安心してご利用いただくために：Japan Toll Receipts はお客様のデータを保持しません。レポートはお客様に直接送信されます。通行料金の請求や個人情報の販売は一切行いません。日本全国のドライバーのための時間節約レポートツールです。',
    statsLabel: 'サポート実績ユーザー数',
    statsDisclosure: 'この数値には、当社が運営する関連ETCサービスを通じてサポートしているユーザーが含まれる場合があります。',
    valueTitle: '時間を無駄にしていませんか？',
    valueSub: '毎週15分の手動確認 = 年間13時間の無駄',
    valueDesc: '公式のETC利用照会サービスにログインして、利用明細を確認して、ダウンロードする… この作業を毎週繰り返していませんか？Japan Toll Receipts なら、登録するだけ。あとは自動でメールに届きます。',
    valuePoints: ['毎週の手動ログインが不要に', 'ダウンロード忘れの心配なし', 'ETC利用証明書とご利用明細が自動で届く', 'ETC利用照会サービスのデータと、それを整理した情報をご提供'],
    howTitle: '4ステップで完了',
    howSub: '複雑な設定は不要です',
    steps: [
      { n: '1', t: '無料アカウント作成', d: 'メールアドレスとパスワードで簡単に登録。月額費用なしで週次レポートを受け取れます。', color: '#9200C7' },
      { n: '2', t: 'セットアップ方法を選択', d: 'ETCカード情報を入力するか、既存のETC利用照会サービスのログイン情報を接続してください。', color: '#0083D3' },
      { n: '3', t: '情報の確認と同意', d: 'ETCカード情報の内容を確認し、当社サービスのお申し込みに同意してください。', color: '#62BB46' },
      { n: '4', t: 'レポートを自動受信', d: '設定したスケジュールに基づいて、ETC利用レポートがメールで届きます。', color: '#FF6B00' },
    ],
    freeTitle: '無料の週次ETC利用レポート',
    freeSub: 'すべての登録ユーザーが利用可能',
    freeDesc: '登録するだけで毎週のETC利用明細レポートをメールで受け取れます。手動でのログインや確認作業は不要です。',
    freeFeats: ['週次自動レポートメール', 'ETCカード最大10枚登録', 'PDF + CSV添付', '15ヶ月超のメール履歴', 'メール言語選択可能'],
    freeCta: '無料で登録する',
    paidTitle: 'Daily Plus (Premium Plan)',
    paidSub: '認証済みETCカード最大10枚まで登録可能',
    paidDesc: '1つのJTR管理アカウントでETCカード最大10枚を登録。すべてのカードの日次・週次・月次レポートを受信。PDF 2種類とCSV形式で配信。ETC利用照会サービスの15ヶ月制限を超えてデータを保持。',
    paidFeats: ['ETCカード最大10枚登録', '日次自動レポート', '週次自動レポート', '月次統合レポート', '日次統合サマリー', '無制限カスタムレポート', 'PDF（2種類）・CSV形式', '15ヶ月超のデータ保持', '優先サポート'],
    paidCta: 'Daily Plus（Premium Plan）を始める',
    paidNote: 'プレミアムプランの月額料金はレポート自動化のサービス料金です。通行料金の請求ではありません。',
    pathsTitle: '2つのセットアップ方法',
    pathsSub: 'お客様の状況に合わせてお選びください',
    pathA: 'ETCカード情報でセットアップ',
    pathADesc: 'ETCカード番号、車載器管理番号、車両番号、最近の利用日を入力。お客様の情報をもとにセットアップをサポートします。',
    pathANote: 'ETC利用照会サービス未登録のお客様向け',
    pathB: '既存のETC利用照会サービスのログイン情報を接続',
    pathBDesc: '公式ETC利用照会サービスに既にアカウントをお持ちの場合、自動レポート配信のみのために接続できます。',
    pathBNote: 'プライバシー重視のお客様向け',
    whyTitle: 'Japan Toll Receiptsを選ぶ理由',
    whySub: 'ETC利用者のための便利な機能',
    whyCards: [
      { icon: Clock, t: '時間の節約', d: '毎回手動でログインする必要がありません。レポートが自動でメールに届きます。', color: '#9200C7' },
      { icon: FileText, t: '分かりやすいレポート', d: '利用日時、IC名、通行料金が見やすい形式で整理されています。', color: '#0083D3' },
      { icon: Globe, t: 'メール言語を選択', d: 'メールは日本語または英語で受信可能。設定でいつでも変更できます。', color: '#62BB46' },
      { icon: Building, t: '法人利用対応', d: 'ETC利用記録を分かりやすく整理したレポート形式。', color: '#FF6B00' },
      { icon: Lock, t: '安全・安心', d: 'ETCカード番号は一部マスク表示。通行料金の請求は行いません。', color: '#0083D3' },
      { icon: Receipt, t: 'PDF/CSV対応', d: 'レポートをPDF・CSV形式で出力可能。表計算ソフトで開けて便利。', color: '#9200C7' },
    ],
    safetyTitle: '独立運営のサービスとして',
    safetyItems: ['公式のETC利用照会サービスではありません', 'NEXCOとは別運営のサービスです', '道路運営会社とは別のサービスです', '通行料金の請求を行いません', '未払い料金の回収を行いません', 'レポート自動化と配信のみをサポート'],
    bizTitle: 'ご利用対象のお客様',
    bizDesc: '日本全国のドライバーや法人・団体向けの自動通行料金レポートサービスです。',
    bizUsers: ['個人ドライバー', '個人事業主', '法人', '車両管理者', '配送・運送事業者', 'SOFA・MOFA・MOC関係者', '外国人居住者', '官公庁・公共部門関係者'],
    faqTitle: 'よくある質問',
    faqs: [
      { q: 'Japan Toll Receiptsは公式のETCサービスですか？', a: 'いいえ。独立したサポートサービスです。公式のETC利用照会サービス、NEXCO、道路事業者ではありません。' },
      { q: '通行料金の請求はありますか？', a: 'いいえ。当サービスは通行料金の請求や未払い料金の回収を一切行いません。' },
      { q: '週次レポートは本当に無料ですか？', a: 'はい。登録ユーザーは月額費用なしで毎週の自動レポートを受け取れます。日本のETC利用者向けのETC利用明細無料メール配信サービスです。無料プランのみでもご利用いただけます。' },
      { q: 'まず何をすればいいですか？', a: 'まずは無料プランでお試しください。無料の週次レポートだけでも十分な価値があります。サービスに慣れ、ご満足いただけたら、必要に応じてDaily Plus（Premium Plan）へのアップグレードをご検討ください。' },
      { q: 'プレミアム機能について教えてください。', a: '現在アーリーアクセス期間中のため、対象のお客様はプレミアム機能を無料でご利用いただけます。体験終了後、アカウントは自動的に無料の Free Weekly プランへ切り替わります。有料プランへのアップグレードは任意で、現在の料金は料金ページでご確認いただけます。' },
    ],
    ctaTitle: '1年間プレミアム無料トライアルを始めましょう',
    ctaSub: '先行受付中のお客様に、プレミアム機能を1年間無料でご案内しています。クレジットカード登録不要。',
    ctaBtn: '無料体験を始める',
    responsibility: ['ETCカードと車載器がお使いの車両に正しく登録されているかご確認ください。', '車載器が以前の車両に登録されたままの場合、通行料金の記録が正しく反映されないことがあります。ETC設定店で再セットアップをお勧めします。'],
  },
  en: {
    heroTag: 'Built on Years of ETC-Card Service Experience',
    heroTitle: 'Your ETC Toll Receipt Platform',
    heroSub: 'Automated toll reports delivered to your email — daily, weekly, or monthly. The first 100,000 verified accounts get 1 full year of premium access free. No credit card required. No obligation.',
    heroCta: 'Try It Free',
    heroCta2: 'See How It Works',
    heroLabels: ['1-Year Premium Free Trial', 'No Credit Card', 'Japanese & English', 'Business & Personal', 'Up to 15 Months History'],
    trust: 'For your safety: Japan Toll Receipts does not store your data. Reports are sent directly to you. We never collect toll payments or sell personal information. We are a time-saving reporting tool for drivers across Japan.',
    statsLabel: 'ETC Customers Supported',
    statsDisclosure: 'This number may include users supported through connected ETC-related services operated by our company.',
    valueTitle: 'What Is Your Time Worth?',
    valueSub: '15 minutes per week × 52 weeks = 13+ hours wasted per year',
    valueDesc: 'Logging into ETC Usage Inquiry Service, navigating Japanese menus, downloading files, translating toll gate names... 15 minutes every week. That\'s over 13 hours a year spent on something that should be automatic. With Japan Toll Receipts, you register once and everything arrives in your inbox.',
    valuePoints: ['No more weekly manual logins to Japanese-only systems', 'Never forget to download — reports arrive automatically', 'ETC usage certificates and details delivered automatically', 'ETC Usage Inquiry Service data, organized for your review'],
    noOneTitle: 'Next-Day Toll Visibility, Done For You',
    noOneDesc: 'Japan\'s toll billing system runs about 2 months behind actual usage, so many drivers wait weeks to review their toll data. We built software that connects to the official ETC Usage Inquiry Service and helps deliver your toll usage as early as the next day.',
    noOneCompare: [
      { label: 'Traditional Japanese System', desc: 'Toll data arrives 2 months late. Japanese-only interface. Manual login required each time.', color: '#ef4444' },
      { label: 'Doing It Yourself', desc: 'Manual logins, manual downloads, and no English summaries — every cycle.', color: '#f59e0b' },
      { label: 'Japan Toll Receipts', desc: 'Next-day toll data delivered to your email. Choose Japanese or English emails. PDF certificates. CSV exports. All automated.', color: '#62BB46' },
    ],
    howTitle: 'Setup Is Simple',
    howSub: 'Register once. Reports arrive automatically.',
    steps: [
      { n: '1', t: 'Create Free Account', d: 'Register with your email and password. Weekly reports are free with no monthly cost.', color: '#9200C7' },
      { n: '2', t: 'Choose Setup Method', d: 'Enter your ETC card information, or sign in with your existing ETC Usage Inquiry Service login.', color: '#0083D3' },
      { n: '3', t: 'Verify & Consent', d: 'Confirm your ETC card information accuracy and provide service consent.', color: '#62BB46' },
      { n: '4', t: 'Receive Reports', d: 'Get ETC usage reports automatically by email on your schedule.', color: '#FF6B00' },
    ],
    freeTitle: 'Free Weekly ETC Usage Reports',
    freeSub: 'Available to all registered users',
    freeDesc: 'Simply register and receive weekly ETC toll usage reports by email. No manual login required.',
    freeFeats: ['Weekly automated report email', 'Up to 10 ETC cards per account', 'PDF + CSV attachments', '15+ months email retention', 'Japanese & English support'],
    freeCta: 'Register Free',
    paidTitle: 'Daily Plus (Premium Plan)',
    paidSub: 'Register up to 10 ETC cards per account',
    paidDesc: 'Register up to 10 ETC cards on one account. Receive daily, weekly, and monthly reports for all cards. Delivered in 2 PDF formats and CSV. Keep data beyond ETC Usage Inquiry Service\'s 15-month limit.',
    paidFeats: ['Up to 10 ETC cards per account', 'Daily automated reports', 'Weekly automated reports', 'Monthly consolidated report', 'Daily consolidated summary', 'Unlimited custom reports', '2 PDF + CSV delivery formats', 'Data retained beyond 15 months', 'Priority support'],
    paidCta: 'Start Daily Plus (Premium Plan)',
    paidNote: 'The monthly Premium fee is for report automation only. It is not a toll payment.',
    pathsTitle: 'Two Setup Methods',
    pathsSub: 'Choose the option that works best for you',
    pathA: 'Set Up with ETC Card Information',
    pathADesc: 'Provide your ETC card number, onboard unit number, license plate, and recent driving date. We will assist with setup.',
    pathANote: 'For customers without a ETC Usage Inquiry Service account',
    pathB: 'Connect Your Existing ETC Usage Inquiry Service Login',
    pathBDesc: 'Already registered with the official service? Connect for automated report delivery only.',
    pathBNote: 'Privacy-friendly option',
    whyTitle: 'Every Report Includes',
    whySub: 'Official data, organized and delivered to your email',
    whyCards: [
      { icon: FileText, t: 'Usage Certificate PDF', d: 'Toll usage certificate retrieved from the ETC Usage Inquiry Service.', color: '#9200C7' },
      { icon: FileText, t: 'Detailed Breakdown PDF', d: 'Individual toll entries with date, time, entry gate, exit gate, and amount for each trip.', color: '#0083D3' },
      { icon: Download, t: 'CSV Export', d: 'Spreadsheet-friendly CSV you can open in any software.', color: '#62BB46' },
      { icon: Globe, t: 'Customizable Email Language', d: 'Choose Japanese or English for your report emails. Toll gate names include both languages. Change anytime in settings.', color: '#FF6B00' },
      { icon: Receipt, t: 'Trip Summaries', d: 'Total amounts, trip counts, discount savings, and date ranges clearly summarized.', color: '#0083D3' },
      { icon: Clock, t: 'Up to 15 Months History', d: 'Access historical toll data going back up to 15 months — the maximum retained by official systems.', color: '#9200C7' },
    ],
    safetyTitle: 'Independent Service',
    safetyItems: ['Not the official ETC usage inquiry service', 'Not NEXCO', 'Not a toll road operator', 'Does not collect toll payments', 'Does not collect unpaid toll charges', 'Only supports report automation and delivery'],
    bizTitle: 'Who Benefits from Toll Reports',
    bizDesc: 'Organized toll records save time and reduce errors for every type of driver.',
    bizUsers: [
      { t: 'Military Personnel', d: 'Travel records for claims' },
      { t: 'Business Travelers', d: 'Tracking corporate toll usage' },
      { t: 'Record Keeping', d: 'Organized toll records for your files' },
      { t: 'Regular Commuters', d: 'Monthly usage tracking & budgeting' },
      { t: 'Vehicle Managers', d: 'Multi-vehicle toll usage overview' },
      { t: 'Contractors', d: 'Toll usage records for travel claims' },
      { t: 'Foreign Residents', d: 'English toll reports in Japan' },
      { t: 'Companies', d: 'Organized toll records for internal review' },
    ],
    faqTitle: 'Frequently Asked Questions',
    faqs: [
      { q: 'Is Japan Toll Receipts the official ETC service?', a: 'No. We are an independent support service. Not the official ETC usage inquiry service, NEXCO, or a toll road operator.' },
      { q: 'Do you collect toll payments?', a: 'No. We never collect toll payments or unpaid toll charges.' },
      { q: 'Is the weekly report really free?', a: 'Yes. Registered users receive weekly automated reports at no cost. This is the first free ETC toll receipt email service in Japan. We never pressure you to upgrade.' },
      { q: 'What should I do first?', a: 'Start with the free plan. The free weekly report alone provides tremendous value. Once you are satisfied, you can decide if you need more frequent reports by upgrading to Daily Plus.' },
      { q: 'What is Daily Plus?', a: 'Daily, weekly, and monthly reports with bilingual formatting. During Early Access, eligible accounts get 1 full year of Premium free — no credit card required during the trial. If you choose not to upgrade afterward, your account automatically continues on the Free Weekly plan, so your service never stops. Any future paid pricing will be announced before the official launch.' },
    ],
    ctaTitle: 'Start Your 1-Year Premium Free Trial',
    ctaSub: 'The first 100,000 verified accounts get full premium access for 1 full year. No credit card required.',
    ctaBtn: 'Start Free Trial',
    responsibility: ['Please make sure your ETC card and onboard unit are properly registered to your current vehicle.', 'If your ETC unit was previously registered to a different vehicle, toll records may not match correctly. Visit an ETC setup shop to update your registration.'],
  },
};

export default function HomePage() {
  const { lang } = useLanguage();
  const t = T[lang];
  const ja = lang === 'ja';
  const prefix = `/${lang}`;
  const [stats, setStats] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [eaCounts, setEaCounts] = useState(null);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/public/stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/api/early-access/counts`).then(r => (r.ok ? r.json() : null)).then(setEaCounts).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* NOTE: Organization + WebSite JSON-LD is emitted centrally by
          SEOHead.jsx for every route. Duplicate emission here was
          removed in the 2026-05-14 schema-audit pass. The old block
          also contained a SearchAction (sitelinks search box) which
          Google deprecated. */}
      <Header />

      {/* First 100,000 Early Access Program — full-width top banner.
          Now mounted globally inside the Header (right under the Coming Soon
          bar) so it appears on every public page. The local copy here is
          removed to avoid double-rendering on /en and /ja home. */}

      {/* Hero — stacked, centered composition. The previous left/right
          split kept producing one tall column and one short one no
          matter how items were rearranged; switching to a single
          centered stack lets every element sit on its own visual line
          with even rhythm and no whitespace asymmetry. */}
      <section className="relative overflow-hidden bg-white border-b-2 border-slate-900 aurora-bg" data-testid="hero-section">
        <span className="float-orb" style={{ width: 360, height: 360, top: -80, left: -120, background: 'radial-gradient(circle, rgba(146,0,199,0.45), transparent 65%)' }} />
        <span className="float-orb" style={{ width: 420, height: 420, top: -40, right: -160, background: 'radial-gradient(circle, rgba(0,131,211,0.40), transparent 65%)', animationDelay: '-5s' }} />
        <span className="float-orb" style={{ width: 300, height: 300, bottom: -80, left: '40%', background: 'radial-gradient(circle, rgba(98,187,70,0.35), transparent 65%)', animationDelay: '-9s' }} />
        {/* One-shot "incoming mail" intro burst — visualizes emails
            arriving the moment the homepage loads. Per user direction:
            faster pace (~2.6s) so the page settles quickly. */}
        <HeroIncomingMail count={70} totalMs={2600} />
        {/* Ambient floating swarm — takes over after the intro, stays
            in the side bands so it never overlaps centre content. 14
            icons split across left/right sides; physics rAF auto-pauses
            when the hero scrolls offscreen. Mouse-repel preserved. */}
        <HeroFloatingMail count={14} delay={2600} />
        {/* Centered, product-led hero (Iter 111) — replaces the asymmetric
            left-text / right-sticker split per customer feedback ("pushed all
            the way to the left … button all the way to the right"). The new
            hero leads with the *service*, not the promo: a centered headline,
            a long professional subheadline, two clear CTAs, a trust line and
            four proof points. Below it, a horizontal ETC Usage Inquiry Service → Report flow
            preview + three sample report cards visually explain what the
            platform delivers. The ¥0 founder sticker + 3-track live spots
            tracker are demoted to a supporting strip lower on the page so
            the trial promo no longer overpowers the product story. */}
        <div className="relative z-[40] max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 md:pt-10 md:pb-10 text-center">
          {/* H1 + highlight line + subheadline (Iter 115 final hero copy
              per customer: "Your ETC toll records, organized automatically." +
              "Daily reports. Monthly summaries. Bilingual receipts.
              Business-ready exports." + paragraph). Eyebrow removed — trust
              statement moved into the line below the CTAs. */}
          <h1 className="text-[28px] sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-slate-900 [text-wrap:balance] mb-5" data-testid="hero-h1">
            {ja ? (
              <>
                {/* JA hero: mobile keeps natural wrapping so kana / kanji
                    never get clipped off the right edge at 320px. Desktop
                    keeps the single-line emphasis via `whitespace-nowrap`. */}
                <span className="block sm:whitespace-nowrap jtr-ja-text">ETC利用履歴を、</span>
                <span className="block sm:whitespace-nowrap jtr-ja-text">
                  <span className="gradient-text-animate">毎日自動配信。</span>
                  <Mail className="inline-block w-[0.85em] h-[0.85em] ml-2 -mt-1 align-middle mail-spin-color" strokeWidth={2.4} aria-hidden="true" />
                </span>
              </>
            ) : (
              <>
                {/* EN hero: same story — at 320px "Your Japan ETC toll
                    records," is wider than the viewport, so we drop
                    whitespace-nowrap on small screens and re-enable it
                    from sm: upwards. */}
                <span className="block sm:whitespace-nowrap">Your ETC toll records,</span>
                <span className="block sm:whitespace-nowrap">
                  <span className="gradient-text-animate">emailed daily.</span>
                  <Mail className="inline-block w-[0.85em] h-[0.85em] ml-2 -mt-1 align-middle mail-spin-color" strokeWidth={2.4} aria-hidden="true" />
                </span>
              </>
            )}
          </h1>

          {/* Highlight line — punchy four-up of deliverables (Iter 118
              correction: reports are 2 PDFs + 1 CSV daily — no Excel). */}
          <p className={`text-lg sm:text-xl lg:text-2xl font-black text-slate-900 leading-snug mx-auto max-w-3xl mb-3 [text-wrap:balance] ${ja ? 'jtr-ja-text' : ''}`} data-testid="hero-highlight">
            {ja
              ? <Jp>毎日、ETC利用照会サービスから取得した利用証明書（PDF）・ご利用明細書（PDF・CSV）をお届けします。</Jp>
              : 'Two PDFs and one CSV, retrieved from the ETC Usage Inquiry Service and organized for you — every day.'}
          </p>

          {/* Advanced-features highlight — per-customer ask: a second
              bold black line under the deliverables sentence that names
              the things JTR actually DOES beyond "emailing PDFs", so
              first-time visitors instantly see the depth of the product.
              Kept one size notch below the primary highlight so the
              hierarchy reads cleanly. Single line, top-level features
              only — no terms that imply real-time visibility (ETC Usage Inquiry Service
              data is next-day) and no insider jargon. */}
          <p
            className={`text-base sm:text-lg lg:text-xl font-black text-slate-900 leading-snug mx-auto max-w-3xl mb-5 [text-wrap:balance] ${ja ? 'jtr-ja-text' : ''}`}
            data-testid="hero-highlight-advanced"
          >
            {ja
              ? <Jp>さらに、JTR独自のサービスとして、スマート通知・登録車両情報との照合・月次サマリー・業務用データ・日英バイリンガル対応を提供します。</Jp>
              : "Plus JTR's own services: smart alerts, registered-vehicle monitoring, monthly summaries, business-ready exports, and bilingual EN/JA."}
          </p>

          {/* Subheadline — leads with JTR's differentiation (emailed
              reports, no daily portal logins, save time) and folds in
              the advanced-features wedge (smart alerts, vehicle detection,
              business exports). Trophy keywords (ETC利用照会サービス /
              ETC利用明細 / PDF / CSV / 15 months) are mentioned NATURALLY
              for SEO relevance, but the lead message is what makes JTR
              unique vs. the official service.
              Iter 391 (2026-05-29). */}
          <p className={`text-base sm:text-lg text-slate-600 leading-relaxed mx-auto max-w-3xl mb-7 [text-wrap:balance] ${ja ? 'jtr-ja-text' : ''}`} data-testid="hero-subhead">
            {ja
              ? <Jp>毎日のログインも、PDFのダウンロードも、エクセル転記も、もう不要です。Japan Toll Receiptsは、ETC利用照会サービスのETC利用明細・利用証明書（PDF・CSV）を自動で取得し、スマート支出アラート・登録車両情報との照合・月次サマリー・業務用エクスポートまでをメールでお届け。個人ドライバー・法人・公共部門の組織・車両管理担当者の毎月の時間を節約します。</Jp>
              : 'No daily portal logins. No manual PDF downloads. No spreadsheet copy-paste. Japan Toll Receipts automatically retrieves your usage statements and PDF/CSV usage certificates from the ETC Usage Inquiry Service, then emails them to you with smart spend alerts, registered-vehicle monitoring, monthly summaries, and business-ready exports — saving personal drivers, businesses, public-sector teams, and vehicle managers hours every month.'}
          </p>

          {/* CTA buttons — "See Our Features" + "See How It Works".
              Transitions tightened: primary uses `btn-premium` only
              (defined in App.css with transform+box-shadow at 140ms)
              and we drop Tailwind's `transition-all hover:scale` so
              we never have two transitions fighting. Secondary uses
              `transition-colors` so only color/bg properties animate,
              not layout — eliminates the perceived hover lag. */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4" data-testid="hero-cta-row">
            <Link
              to={`${prefix}/features`}
              className="inline-flex items-center justify-center w-full sm:w-auto py-4 px-8 rounded-2xl text-white font-extrabold text-lg sm:text-xl btn-premium jtr-cta-sheen"
              style={{ background: 'linear-gradient(rgb(190, 80, 255) 0%, rgb(146, 0, 199) 45%, rgb(110, 0, 150) 100%)', boxShadow: 'rgba(146, 0, 199, 0.4) 0px 6px 20px, rgba(0, 0, 0, 0.15) 0px 2px 6px, rgba(255, 255, 255, 0.3) 0px 1px 0px inset', textShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px' }}
              data-testid="hero-cta-primary"
            >
              {ja ? '機能を見る' : 'See Our Features'}
              <ArrowRight className="w-5 h-5 ml-2 text-white" strokeWidth={2.6} />
            </Link>
            <Link
              to={`${prefix}/how-it-works`}
              className="inline-flex items-center justify-center w-full sm:w-auto py-4 px-8 rounded-2xl text-white font-extrabold text-lg sm:text-xl btn-premium"
              style={{ background: 'linear-gradient(rgb(92, 255, 92) 0%, rgb(0, 200, 0) 40%, rgb(0, 154, 0) 100%)', boxShadow: 'rgba(0, 200, 0, 0.4) 0px 6px 20px, rgba(0, 0, 0, 0.15) 0px 2px 6px, rgba(255, 255, 255, 0.3) 0px 1px 0px inset', textShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px' }}
              data-testid="hero-cta-secondary"
            >
              {ja ? 'ご利用の流れ' : 'See How It Works'}
              <ChevronRight className="w-5 h-5 ml-1 text-white" strokeWidth={2.6} />
            </Link>
            {/* Engineering Architecture — third hero CTA (blue) routes to the
                technical Engineering Trust Center (/architecture) so reviewers
                can inspect the full safety/scaling model from the top. The
                purple → green → blue trio gives the hero a clear, branded
                CTA rhythm. */}
            <Link
              to={`${prefix}/architecture`}
              className="group inline-flex items-center justify-center w-full sm:w-auto py-4 px-8 rounded-2xl text-white font-extrabold text-lg sm:text-xl btn-premium"
              style={{ background: 'linear-gradient(rgb(74, 184, 255) 0%, rgb(0, 131, 211) 45%, rgb(0, 95, 160) 100%)', boxShadow: 'rgba(0, 131, 211, 0.4) 0px 6px 20px, rgba(0, 0, 0, 0.15) 0px 2px 6px, rgba(255, 255, 255, 0.3) 0px 1px 0px inset', textShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px' }}
              data-testid="hero-cta-architecture"
            >
              <Workflow className="w-5 h-5 mr-2 text-white" strokeWidth={2.4} />
              {ja ? 'システム構成・技術解説' : 'Engineering Architecture'}
              <ArrowRight className="w-5 h-5 ml-2 text-white group-hover:translate-x-0.5 transition-transform" strokeWidth={2.6} />
            </Link>
          </div>

          {/* "Founder's Message" — slim link to the founder narrative page.
              Lives just under the hero CTAs so curious visitors can
              learn WHY JTR was created without leaving the homepage
              hero. Intentionally low-key — single text link with a
              subtle violet treatment so it doesn't compete with the
              two primary CTAs above. */}
          {/* Founder's Message — small text link supporting trust.
              Lives just under the hero CTAs so curious visitors can
              learn WHY JTR was created without leaving the homepage
              hero. Intentionally low-key: small text + BookOpen icon,
              never a big button, so it doesn't compete with the two
              primary conversion CTAs above. */}
          <div className="text-center mb-6" data-testid="hero-our-story-link">
            <Link
              to={`/${lang}/founders-message`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:text-fuchsia-700 transition-colors group"
            >
              <BookOpen className="w-4 h-4 opacity-70 group-hover:opacity-100" strokeWidth={2} />
              <span className="underline decoration-violet-300 decoration-2 underline-offset-4 group-hover:decoration-fuchsia-400">
                {ja ? '創業者メッセージ：なぜJapanTollReceipts.jpを創ったのか →' : 'Founder\u2019s Message: Why we created JapanTollReceipts.jp →'}
              </span>
            </Link>
          </div>

          {/* Trust block — pair of brand badges that visualise the
              "13 years in market · made in Japan" claim. The sentence
              below NO LONGER repeats the year / origin (the badges
              say it loudly already); it only adds the audience
              context — who JTR is built for. */}
          <div
            className="flex flex-col items-center gap-3 mb-6"
            data-testid="hero-trust-block"
          >
            <div
              className="inline-flex flex-wrap items-center justify-center gap-2"
              data-testid="hero-trust-badges"
            >
              {/* Years-in-service badge — emerald shield, locked years
                  count so the "since 2013" claim sticks at a glance. */}
              <span
                className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-[11px] font-black tracking-wide shadow-md ring-1 ring-emerald-700/30"
                data-testid="hero-trust-badge-years"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20 ring-1 ring-white/40">
                  <ShieldCheck className="w-3 h-3" strokeWidth={2.8} aria-hidden="true" />
                </span>
                <span className="uppercase">
                  {ja ? 'NoJo Enterprise' : 'NOJO ENTERPRISE'}
                </span>
                <span className="opacity-70" aria-hidden="true">·</span>
                <span className="uppercase">
                  {ja ? 'ETC事業2013年〜' : 'ETC SINCE 2013'}
                </span>
              </span>

              {/* Origin badge — slate pill with an inline Japan flag
                  SVG so the "made in Japan" claim has a real visual
                  anchor instead of just text. The flag is two
                  vector layers (white field + crimson sun) — no
                  external image, no emoji rendering inconsistency. */}
              <span
                className="inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1 rounded-full bg-slate-900 text-white text-[11px] font-black tracking-wide shadow-md ring-1 ring-slate-700"
                data-testid="hero-trust-badge-origin"
              >
                <svg
                  viewBox="0 0 30 20"
                  className="w-5 h-[14px] rounded-sm overflow-hidden ring-1 ring-white/30"
                  aria-label={ja ? '日本国旗' : 'Flag of Japan'}
                  role="img"
                >
                  <rect width="30" height="20" fill="#ffffff" />
                  <circle cx="15" cy="10" r="6" fill="#BC002D" />
                </svg>
                <span className="uppercase">
                  {ja ? '日本で開発・運営' : 'Developed in Japan'}
                </span>
              </span>
            </div>
            {/* Audience-only line — no year, no origin (the badges
                cover those). Keeps the trust block from sounding
                like an echo of itself. */}
            <p className={`text-xs sm:text-sm font-bold text-slate-500 [text-wrap:balance] ${ja ? 'jtr-ja-text' : ''}`} data-testid="hero-trust-line">
              {ja
                ? '個人ドライバー・法人・車両管理担当者まで幅広く対応。'
                : 'Built for individual drivers, businesses, and vehicle managers.'}
            </p>
          </div>

          {/* ── Early Access Program live-spots strip — Iter 119: ¥0 sticker now
              opens a punchy HoverCard "founder benefits" pop-out on hover
              (or tap on touch). Designed to *jump out* — gold/emerald
              gradient, sparkle accents, animated entry — and explains the
              prototype-reward rationale: founders get exclusive locked-in
              pricing once we go live, no obligation, no risk, no credit
              card, just feedback. The sticker itself remains a Link to
              /free-trial — the HoverCard adds context without breaking the
              tap-to-claim flow. */}
          <div className="max-w-5xl mx-auto mt-12 sm:mt-16 mb-8" data-testid="hero-founder-strip">
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
              {/* LEFT — Big clickable ¥0 sticker (the circle is the hover
                  trigger; text below is a separate Link so hovering it does
                  NOT pop the founder card). Iter 120 customer ask. */}
              <div className="lg:col-span-5 flex flex-col items-center text-center">
                <StickyEarlyAccessSticker ja={ja} prefix={prefix} />

                {/* Text below — separate Link, NOT inside HoverCardTrigger so
                    hovering this doesn't pop the founder card (Iter 120). */}
                <Link to={`${prefix}/free-trial`} className="mt-4 sm:mt-5 max-w-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-xl" data-testid="hero-founder-text-link">
                  <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.22em] text-emerald-700 mb-1.5">
                    {ja ? '先行利用プログラム' : 'Early Access Program'}
                  </p>
                  <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">
                    {ja ? 'プレミアム1年間無料' : '1-Year Premium, free'}
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm font-bold text-slate-600 leading-snug">
                    {ja
                      ? '無料体験中はクレジットカード登録不要。1 年間の体験終了後は Free Weekly に自動切替。アップグレードを希望される場合のみカード登録が必要です。'
                      : 'No credit card needed during the free trial. When the 1-year trial ends, your account auto-downgrades to Free Weekly. A credit card is only required if you choose to upgrade.'}
                  </p>
                </Link>
              </div>

              {/* RIGHT — Live spots remaining (large tiles) */}
              <div className="lg:col-span-7 rounded-3xl bg-white ring-1 ring-slate-200 shadow-xl shadow-slate-900/[0.06] p-5 sm:p-6" data-testid="hero-founder-tracker">
                <div className="flex items-center justify-between mb-4">
                  <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-black uppercase tracking-[0.22em] text-emerald-700">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
                      <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-500" />
                    </span>
                    {ja ? '受付可能枠数' : 'Live spots remaining'}
                  </span>
                  <Link to={`${prefix}/free-trial`} className="inline-flex items-center gap-1 min-h-[44px] py-2 text-[11px] sm:text-xs font-black uppercase tracking-wider text-emerald-700 hover:text-emerald-900" data-testid="hero-founder-claim">
                    {ja ? '無料で始める' : 'Reserve my free access'}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.6} />
                  </Link>
                </div>
                {(() => {
                  const total = eaCounts?.total;
                  const remaining = total ? total.remaining : 100000;
                  const limit = total ? total.limit : 100000;
                  const isFull = total ? total.full : false;
                  const claimed = total ? total.confirmed : 0;
                  const pct = limit > 0 ? Math.min(100, Math.max(1.5, (claimed / limit) * 100)) : 0;
                  const types = [
                    { label: ja ? '個人' : 'Personal',   Icon: Users,       color: 'text-emerald-600', href: `${prefix}/personal` },
                    { label: ja ? '法人' : 'Business',   Icon: Building,     color: 'text-purple-600',  href: `${prefix}/business` },
                    { label: ja ? '官公庁' : 'Gov & Public', Icon: ShieldCheck, color: 'text-sky-700',     href: `${prefix}/government` },
                  ];
                  return (
                    <>
                      <Link to={`${prefix}/free-trial`} className="block rounded-2xl bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-100 hover:ring-emerald-300 transition px-5 py-6 text-center" data-testid="hero-founder-tracker-combined">
                        <div className="text-5xl sm:text-6xl font-black tabular-nums leading-none text-emerald-700" data-testid="hero-founder-tracker-count-total">
                          {isFull ? (ja ? '満員' : 'Full') : remaining.toLocaleString()}
                        </div>
                        <div className="mt-2 text-[11px] sm:text-xs font-black uppercase tracking-wider text-slate-500">
                          {ja ? `／ ${limit.toLocaleString()} 名の先行受付枠 残り` : `of ${limit.toLocaleString()} Early Access spots left`}
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-emerald-100 overflow-hidden max-w-xs mx-auto">
                          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                        </div>
                      </Link>
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {types.map((t, i) => (
                          <Link key={i} to={t.href} className="rounded-xl bg-slate-50 ring-1 ring-slate-100 hover:ring-slate-300 px-2 py-3 flex flex-col items-center gap-1 transition" data-testid={`hero-founder-type-${i}`}>
                            <t.Icon className={`w-5 h-5 ${t.color}`} strokeWidth={2.4} />
                            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-500">{t.label}</span>
                          </Link>
                        ))}
                      </div>
                    </>
                  );
                })()}
                <p className="mt-4 text-[11px] sm:text-xs text-slate-500 text-center leading-snug">
                  {ja
                    ? '先行受付中のお客様に、1年間のプレミアム無料体験をご案内しています。個人・法人・官公庁いずれも対象で、アカウントタイプによってご利用いただける機能が異なります（枠の数とは関係ありません）。体験終了後、アカウントは自動的に無料のFree Weeklyへ切り替わります。有料プランへのアップグレードは任意で、現在の料金は料金ページでご確認いただけます。'
                    : 'Early access is open. Eligible members can receive a 1-Year Premium free trial. Personal, Business, and Government are all eligible — your account type determines features, not how many spots are used. After the 1-year trial, your account automatically moves to Free Weekly; paid upgrades are optional, and current paid-plan pricing is on our Pricing page.'}
                </p>
                <Suspense fallback={<div className="h-16" aria-hidden="true" />}>
                  <LaunchProgramStrip />
                </Suspense>
              </div>
            </div>
          </div>

          {/* ── Toll Trip Status Checker — interactive SEO conversion
              widget (Iter 391, 2026-05-29). Captures the high-volume
              "ETC利用明細 反映されない" / "Did my ETC trip post yet?"
              queries that drive impressions to JTR but currently lose
              the click to the official ETC利用照会サービス. Embedded
              here so it lives above the fold on second scroll. */}
          <div className="my-12 sm:my-16" data-testid="hero-trip-checker">
            <TollTripStatusChecker variant="compact" />
          </div>

          {/* 4 proof points — centered grid, each with a hover tooltip */}
          <TooltipProvider delayDuration={150}>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto text-left" data-testid="hero-proof-points">
            {[
              {
                en: 'Daily reports before 10:00 JST', ja: '毎朝10:00までに配信',
                tipEn: 'JTR signs in to the ETC Usage Inquiry Service for you every morning and emails yesterday\'s ETC totals before 10:00 JST — automatically, with no logging in on your side.',
                tipJa: 'JTR が毎朝お客様の代わりに ETC利用照会サービス へサインインし、午前10:00 までに前日の ETC 利用合計をメール配信。お客様ご自身でのログイン操作は一切不要です。',
              },
              {
                en: 'Bilingual email · Japanese PDFs', ja: '日英バイリンガルメール・日本語PDF',
                tipEn: 'The PDF and CSV attachments are the usage details and certificates retrieved from the ETC Usage Inquiry Service, always in Japanese — we never modify them. The email body is bilingual: based on your language setting, JTR summarizes your drives, tolls, and totals in English or Japanese.',
                tipJa: 'PDFとCSVの添付ファイルは、ETC利用照会サービスから取得した利用明細・利用証明書で、常に日本語です（当社では内容を変更しません）。メール本文は、お客様の言語設定に応じて、走行区間・各通行料金・合計を日本語または英語で整理してお届けします。',
              },
              {
                en: '2 PDFs + 1 CSV every day', ja: '毎日PDF 2種類・CSV 1種類',
                tipEn: 'Every day you receive a usage-certificate PDF, an itemized usage-statement PDF, and a spreadsheet-friendly usage-details CSV retrieved from the ETC Usage Inquiry Service. No Excel needed — open the CSV anywhere.',
                tipJa: '毎日、ETC利用照会サービスから取得した利用証明書PDF、ご利用明細書PDF、表計算ソフト向けの利用明細CSVをお届け。Excel は不要 — CSV はどこでも開けます。',
              },
              {
                en: 'Personal · Business · Organizations · Vehicle managers', ja: '個人・法人・組織・車両管理担当者',
                tipEn: 'The same JTR system serves every audience — personal drivers, businesses, vehicle managers, and public-sector organizations. Pick the track that matches you.',
                tipJa: '同じ JTR のシステムが全対象ユーザーに対応 — 個人ドライバー・法人・車両管理者・公共部門の組織。お客様に合うコースをお選びください。',
              },
            ].map((b, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <li className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-white/70 ring-1 ring-slate-200 hover:ring-emerald-400 hover:bg-white transition-all cursor-help" data-testid={`hero-proof-${i}`}>
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 mt-0.5">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </span>
                    <span className="text-[12.5px] sm:text-sm font-bold text-slate-800 leading-snug">{ja ? b.ja : b.en}</span>
                  </li>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] bg-slate-900 text-slate-50 border-slate-700 text-[12.5px] leading-relaxed font-medium shadow-xl">
                  {ja ? b.tipJa : b.tipEn}
                </TooltipContent>
              </Tooltip>
            ))}
          </ul>
          </TooltipProvider>
        </div>

        {/* ── Dashboard preview row — explains the service visually.
            Iter 118: each flow step + each sample report card now opens a
            tooltip on hover (long-press on touch) explaining what happens
            in plain language. File format claim corrected: reports are
            2 PDFs + 1 CSV daily (no Excel). */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-testid="hero-dashboard-preview">
          {/* Horizontal ETC Usage Inquiry Service → Report flow */}
          <TooltipProvider delayDuration={150}>
          <div className="rounded-3xl bg-white ring-1 ring-slate-200 shadow-xl shadow-slate-900/[0.06] p-5 sm:p-7">
            <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.28em] text-emerald-700 text-center mb-4">
              {ja ? '接続から配信まで（タップすると詳細を確認できます）' : 'From connection to delivery — tap or hover for details'}
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hero-flow-steps" data-reveal-stagger>
              {[
                {
                  Icon: CreditCard, tone: 'emerald',
                  en: 'ETC Card Connected',  ja: 'ETCカード登録済み',
                  tipEn: 'Tell JTR which ETC card you drive with — we only need the last 4 digits + your ETC Usage Inquiry Service sign-in. No PIN, no full card number, never stored long-term.',
                  tipJa: 'お使いの ETC カードを JTR にお知らせください — 必要なのは下 4 桁と ETC利用照会サービス のログイン情報のみ。暗証番号や全番号は不要、長期保存もしません。',
                },
                {
                  Icon: Link2, tone: 'sky',
                  en: 'Toll data retrieved for you',  ja: 'JTRが通行料データを代行取得',
                  tipEn: 'Every morning before 10:00 JST, JTR signs in to the ETC Usage Inquiry Service on your behalf and pulls yesterday\'s toll usage — retrieved and delivered, never stored long-term, never permanently stored on JTR.',
                  tipJa: '毎朝 午前10:00 までに、JTR がお客様の代理で ETC利用照会サービス にサインインし、前日の通行料データを取得 — 完全パススルーで JTR には継続保存しない設計です。',
                },
                {
                  Icon: Workflow, tone: 'purple',
                  en: 'Daily Report Generated',  ja: '日次レポート作成',
                  tipEn: 'JTR organizes every toll, every IC, and every plate-matched vehicle into a bilingual report. Smart spend alerts, registered-vehicle checks, and totals organized for internal review run here.',
                  tipJa: 'すべての通行料・IC・登録車両との照合を日本語・英語レポートに整理。スマート支出アラート・登録車両チェック・集計もここで処理します。',
                },
                {
                  Icon: Download, tone: 'amber',
                  en: 'Daily ETC documents delivered',  ja: '毎日ETC利用データをお届け',
                  tipEn: 'Your inbox receives two PDFs and one CSV every day — all three are the original documents from the ETC Usage Inquiry Service in Japanese. The bilingual summary of your drives, tolls, and totals lives in the email body, generated in your selected language.',
                  tipJa: '毎日、PDF 2 種類 + CSV 1 種類をメールでお届け — 3 ファイルすべて ETC利用照会サービス から取得した日本語の原本です。走行区間・通行料金・合計の日本語・英語サマリーは、設定言語に応じてメール本文に記載します。',
                },
              ].map(({ Icon, en, ja: jaLbl, tone, tipEn, tipJa }, i) => (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="relative flex items-center gap-3 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-3 py-3 text-left hover:ring-slate-400 hover:bg-white transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      data-testid={`hero-flow-step-${i + 1}`}
                    >
                      <span className={`flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl text-white ${
                        tone === 'emerald' ? 'bg-emerald-500' :
                        tone === 'sky'     ? 'bg-sky-500'     :
                        tone === 'purple'  ? 'bg-purple-500'  :
                                              'bg-amber-500'
                      }`}>
                        <Icon className="w-5 h-5" strokeWidth={2.4} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                          {ja ? `ステップ ${i + 1}` : `Step ${i + 1}`}
                        </div>
                        <div className="text-sm sm:text-[15px] font-extrabold text-slate-900 leading-tight">
                          {ja ? jaLbl : en}
                        </div>
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[300px] bg-slate-900 text-slate-50 border-slate-700 text-[13px] leading-relaxed font-medium shadow-xl">
                    {ja ? tipJa : tipEn}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>

            {/* 3 sample report cards — also have tooltips */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5" data-testid="hero-sample-reports" data-reveal-stagger>
              {/* Today's ETC Usage */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4 text-left hover:ring-emerald-400 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" data-testid="hero-sample-today">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                        <Receipt className="w-3.5 h-3.5" strokeWidth={2.6} />
                        {ja ? '本日のETC利用' : "Today's ETC usage"}
                      </span>
                      <span className="text-[9px] font-bold text-emerald-600">PDF</span>
                    </div>
                    <div className="text-2xl font-black text-emerald-900 tabular-nums leading-none">¥8,420</div>
                    <div className="mt-1.5 text-[11px] font-bold text-emerald-800">{ja ? '3件の通行料' : '3 toll segments'}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] bg-slate-900 text-slate-50 border-slate-700 text-[13px] leading-relaxed font-medium shadow-xl">
                  {ja
                    ? '前日のETC利用合計と各通行明細を、ETC利用照会サービスから取得した日本語PDFで添付。メール本文には設定言語（日本語・英語）で利用集計を記載します。記録の保管にもご利用いただけます。'
                    : 'Yesterday\'s ETC total + the per-toll breakdown. Attached as a Japanese PDF retrieved from the ETC Usage Inquiry Service; the email body summarizes the same data in your chosen language (English or Japanese) — for your records and receipt storage.'}
                </TooltipContent>
              </Tooltip>

              {/* Registered Vehicle Check */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-4 text-left hover:ring-amber-400 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" data-testid="hero-sample-vehicle-check">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-700">
                        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.6} />
                        {ja ? '車両チェック' : 'Vehicle check'}
                      </span>
                      <span className="text-[9px] font-bold text-amber-600">{ja ? '要確認' : 'Review'}</span>
                    </div>
                    <div className="text-2xl font-black text-amber-900 leading-none">{ja ? '1件' : '1 alert'}</div>
                    <div className="mt-1.5 text-[11px] font-bold text-amber-800">{ja ? '登録リスト外' : 'Outside authorized list'}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] bg-slate-900 text-slate-50 border-slate-700 text-[13px] leading-relaxed font-medium shadow-xl">
                  {ja
                    ? '登録車両リストとETCカードの実際の利用車両を毎日照合。リスト外の利用があった場合、翌日のレポートに表示します。JTRが自動的に不正と判定することはありません。'
                    : 'Every day we cross-check the cards driven against your registered-vehicle list. Any out-of-list activity surfaces in the next day\'s report for human review — never automatically judged as fraud by JTR.'}
                </TooltipContent>
              </Tooltip>

              {/* Monthly Report */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="rounded-2xl bg-purple-50 ring-1 ring-purple-200 p-4 text-left hover:ring-purple-400 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500" data-testid="hero-sample-monthly">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-purple-700">
                        <Calendar className="w-3.5 h-3.5" strokeWidth={2.6} />
                        {ja ? '月次レポート' : 'Monthly report'}
                      </span>
                      <span className="text-[9px] font-bold text-purple-600">PDF</span>
                    </div>
                    <div className="text-2xl font-black text-purple-900 leading-none">{ja ? '4月版' : 'April'}</div>
                    <div className="mt-1.5 text-[11px] font-bold text-purple-800">{ja ? '配信準備完了' : 'Ready to deliver'}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px] bg-slate-900 text-slate-50 border-slate-700 text-[13px] leading-relaxed font-medium shadow-xl">
                  {ja
                    ? '毎月の利用合計を、カード別・車両別にETC利用照会サービスから取得したPDF形式で翌月1日に配信。メール本文には選択言語で利用集計を記載し、記録整理・社内確認にご利用いただけます。'
                    : 'Every month\'s totals — by card, by vehicle — delivered on the first of the following month as a Japanese PDF retrieved from the ETC Usage Inquiry Service. The email body adds a summary in your chosen language, organized for your records and internal review.'}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          </TooltipProvider>
        </div>

        {/* ── EVERY PLAN INCLUDES — Mint Mobile-style chunky black strip ── */}
        <div className="bg-slate-900 text-white border-t border-slate-900" data-testid="hero-every-plan-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <p className="text-[10px] sm:text-xs font-black uppercase tracking-[0.28em] text-emerald-300 whitespace-nowrap">
                {ja ? '全プラン共通' : 'Every plan includes'}
              </p>
              <div className="flex items-center gap-x-6 gap-y-2 flex-wrap text-[11.5px] sm:text-sm font-bold">
                {[
                  { Icon: Mail,        en: 'Bilingual email summary',    ja: '日英バイリンガルメールサマリー' },
                  { Icon: Calendar,    en: 'Daily · Weekly · Monthly',   ja: '日次・週次・月次' },
                  { Icon: Bell,        en: 'Smart Spend Alerts',         ja: 'スマート支出アラート' },
                  { Icon: ShieldCheck, en: 'Registered Vehicle Checks',  ja: '登録車両チェック' },
                  { Icon: Lock,        en: 'Pass-through security',      ja: 'パススルー認証' },
                  { Icon: Globe,       en: 'No credit card',             ja: 'クレジットカード登録不要' },
                ].map(({ Icon, en, ja: jaLbl }, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2.4} />
                    {ja ? jaLbl : en}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Trust/discover row (kept). The previous ProductSlideshow +
            SpendAlertsShowcaseSlideshow components were removed per Iter 118
            customer feedback — they will be replaced later by a more
            interactive walk-through or short video. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
          <div className="mt-6 mb-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-full" data-testid="hero-trust-no-card">
              <Shield className="w-4 h-4 text-[#9200C7] flex-shrink-0" strokeWidth={1.75} />
              <span className="text-xs sm:text-sm font-bold text-[#9200C7]">{ja ? '無料体験中はクレジットカード登録不要' : 'No credit card required for the free trial'}</span>
            </div>
            <Link to="/personal" className="inline-flex items-center gap-1 min-h-[44px] py-2 text-xs sm:text-sm font-bold text-emerald-700 hover:text-emerald-800 hover:underline" data-testid="hero-discover-personal">
              {ja ? '個人プラン' : 'For Individuals'}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            </Link>
            <Link to="/business" className="inline-flex items-center gap-1 min-h-[44px] py-2 text-xs sm:text-sm font-bold text-[#9200C7] hover:text-[#7a00a8] hover:underline" data-testid="hero-discover-business">
              {ja ? '法人プラン' : 'For Business'}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            </Link>
            <Link to="/government" className="inline-flex items-center gap-1 min-h-[44px] py-2 text-xs sm:text-sm font-bold text-sky-700 hover:text-sky-800 hover:underline" data-testid="hero-discover-government">
              {ja ? '官公庁プラン' : 'For Gov & Public-Sector'}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar — non-price proof points (homepage stays pricing-free) */}
      <section className="bg-white border-b border-gray-100 py-10 animate-fade-in" data-testid="trust-stats-section" data-reveal-stagger>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-reveal-stagger>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-[#9200C7]" data-testid="stat-users">
                {(Math.floor(Math.max(stats?.registered_users || 0, 17700) / 100) * 100).toLocaleString() + '+'}
              </p>
              <p className="text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-wider">{t.statsLabel}</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-emerald-600">99.97<span className="text-base font-normal text-gray-400">%</span></p>
              <p className="text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-wider">{lang === 'ja' ? '配信稼働率' : 'Delivery uptime'}</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-[#0083D3]">31<span className="text-base font-normal text-gray-400">{lang === 'ja' ? '項目' : ' checks'}</span></p>
              <p className="text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-wider">{lang === 'ja' ? '送信前検証' : 'Pre-send validation'}</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-[#FF6B00]" data-testid="stat-reports">
                {stats?.reports_generated > 0 ? stats.reports_generated.toLocaleString() : '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-wider">{lang === 'ja' ? 'レポート配信数' : 'Reports delivered'}</p>
            </div>
          </div>
          {stats?.has_external_data && (
            <p className="mt-5 text-center text-[10px] text-gray-400">{t.statsDisclosure}</p>
          )}
        </div>
      </section>

      {/* Trust Notice */}
      <section className="bg-emerald-50/60 py-4 border-b border-emerald-100" data-testid="trust-notice">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" strokeWidth={2} />
          <div className="flex-1">
            <p className="text-[11px] sm:text-xs leading-relaxed">
              <span className="font-black uppercase tracking-wider text-emerald-700 mr-1.5">{ja ? '重要：' : 'Important:'}</span>
              <span className="font-bold text-gray-900">
                {ja
                  ? 'JTRは通行料金の決済を一切扱いません。支払いを求めるメールは100％詐欺です。'
                  : 'JTR never handles toll payments. Any email demanding payment is 100% a scam.'}
              </span>
              <span className="text-gray-600 ml-1">
                {ja
                  ? 'ETC利用明細を取得してPDFとCSVでメール配信する自動化サービスです。通行料金の徴収や決済処理は一切行いません。'
                  : 'We fetch your ETC usage detail and email it to you as PDFs and CSV. We do not collect tolls or process payments.'}
              </span>
            </p>
            <p className="text-[10px] text-gray-500 leading-relaxed mt-1.5">{t.trust}</p>
          </div>
        </div>
      </section>

      {/* Value Section - Purple overlay image (matching nojo "Save Up to 40%") */}
      <section className="relative overflow-hidden aurora-bg" data-testid="value-section">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_f1c3566d-6c14-41e1-9328-caca9b9c3b06/artifacts/xb1k9onc_%7B4D8948DA-8794-4FD0-8EB6-EA5AEC737B26%7D.png" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#9200C7]/90 via-[#9200C7]/80 to-[#9200C7]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{t.valueTitle}</h2>
              <p className="mt-4 text-lg text-white/90 leading-relaxed">{t.valueDesc}</p>
              <ul className="mt-6 space-y-3">
                {t.valuePoints.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-white/90">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    {p}
                  </li>
                ))}
              </ul>
              <Link to={`${prefix}/free-trial`}>
                <button className="mt-8 px-8 py-3 rounded-full bg-white text-[#9200C7] font-bold text-base hover:bg-gray-100 shadow-xl transition-all hover:-translate-y-0.5 flex items-center gap-2">
                  {ja ? '無料ではじめる' : 'Start Free Trial'} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
            {/* Stats card - solid background, no blur */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <p className="text-sm text-gray-500 text-center">{ja ? '年間削減時間' : 'Typical time savings per year'}</p>
              <p className="text-6xl sm:text-7xl font-extrabold text-[#9200C7] text-center mt-2">13<span className="text-3xl">{ja ? '時間+' : 'hrs+'}</span></p>
              <p className="text-sm text-gray-500 text-center mt-2">{ja ? '手作業から解放' : 'freed from manual checking'}</p>
              <div className="mt-6 space-y-3">
                {[
                  { label: ja ? '配信稼働率' : 'Delivery uptime', value: '99.97%', color: '#62BB46' },
                  { label: ja ? '送信前検証' : 'Pre-send checks', value: ja ? '31項目' : '31-point', color: '#0083D3' },
                  { label: ja ? 'データセキュリティ' : 'Data Safety', value: '100%', color: '#9200C7' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-t border-gray-100">
                    <span className="text-sm text-gray-600">{row.label}</span>
                    <span className="text-sm font-bold" style={{ color: row.color }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-20 bg-white" data-testid="how-it-works-section" data-reveal="focus">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#9200C7]">{t.howTitle}</h2>
            <p className="mt-3 text-base text-gray-500">{t.howSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {t.steps.map((step, i) => (
              <div key={i} className="relative text-center group">
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-5 shadow-lg transition-transform group-hover:-translate-y-1" style={{ backgroundColor: step.color }}>
                  {step.n}
                </div>
                <h3 className="text-base font-bold mb-2" style={{ color: step.color }}>{step.t}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.d}</p>
                {i < 3 && <div className="hidden md:block absolute top-8 right-0 w-8 h-0.5 bg-gray-200 translate-x-4" />}
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to={`${prefix}/how-it-works`}>
              <Button variant="outline" className="border-2 border-[#9200C7] text-[#9200C7] hover:bg-purple-50 rounded-full px-6 py-2.5 h-auto text-sm font-semibold" data-testid="how-it-works-link">
                {lang === 'ja' ? '詳しく見る' : 'Learn More'} <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Live operations showcase — multi-system polling slideshow */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-slate-950 via-slate-900 to-[#0d1f1a] border-y border-slate-800 aurora-bg" data-testid="home-ai-pipeline-section" data-reveal="focus">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300 mb-2">
              {lang === 'ja' ? '稼働状況を見る' : 'See us live'}
            </p>
            <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
              {lang === 'ja' ? 'いま、日本チームが稼働中です。'
                            : 'Our Japan team is running — right now.'}
            </h2>
            <p className="text-sm text-slate-300 mt-3 max-w-2xl mx-auto leading-relaxed">
              {lang === 'ja' ? '全国のお客様へ月次レポートを届けている運用チームと自動化システムをご覧ください。お支払いいただいた価値を、そのまま可視化しています。'
                            : "Below is the operations team and automation keeping every monthly toll report flowing across Japan — exactly what you're paying for, fully visible."}
            </p>
          </div>
          <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
            <AIShowcaseSlideshow variant="personal" />
          </Suspense>
        </div>
      </section>

      {/* ── WHAT CAN JTR DO? — Top features one-pager ─────────────────
          Replaces the old "Capture → AI → Pass-through → Delivery"
          HomepageFlowScenes animation per direct customer feedback
          (Iter 108). The new section uses the visitor's exact requested
          headline + a single-line capability sweep so the page can be
          scanned in 3 seconds before they decide to dig deeper. The
          "See all features →" CTA below links to the full /features hub. */}
      <section className="py-12 sm:py-20 bg-white border-t border-slate-100" data-testid="home-features" data-reveal="focus">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-3">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700">
              {ja ? 'プラットフォーム機能' : 'Platform capabilities'}
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-[1.1]">
              {ja ? 'JTRで何ができる？' : 'What can JTR do?'}
            </h2>
          </div>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mb-12">
            {ja
              ? '日次ETCレポート、スマート支出アラート、経費確認用の資料、登録車両情報の確認、ETC利用照会サービスからの利用明細取得の自動化、複数車両のETC利用確認——すべて一つのプラットフォームで。'
              : 'Daily ETC reports, smart alerts, records organized for expense review, registered-vehicle information matching, automated retrieval from the ETC Usage Inquiry Service, and multi-vehicle ETC usage review — from one platform.'}
          </p>
          <p className="text-[11px] sm:text-xs text-slate-500 italic text-center -mt-8 mb-10">
            {ja
              ? '「JTR」はJapan Toll Receiptsの略称です。お客様からもこの名前で呼ばれています。'
              : '"JTR" is shorthand for Japan Toll Receipts — that\'s how our customers refer to us.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                slug: 'daily-etc-reports', Icon: Calendar, color: '#0083D3',
                en: 'Daily ETC Reports', ja: '日次ETCレポート',
                bodyEn: 'PDF + CSV in your inbox before 10:00 JST every morning. No ETC Usage Inquiry Service logins.',
                bodyJa: '毎朝午前10:00までにPDF・CSVをメール配信。ETC利用照会サービスへのログインは不要です。',
              },
              {
                slug: 'smart-alerts', Icon: Bell, color: '#F97316',
                en: 'Smart Spend Alerts', ja: 'スマート支出アラート',
                bodyEn: 'Per-card thresholds. Get pinged when daily, weekly, or monthly toll spend crosses your line.',
                bodyJa: 'カード別しきい値。日次・週次・月次の通行料がラインを超えたら即通知。',
              },
              {
                slug: 'reimbursement-reports', Icon: FileText, color: '#10B981',
                en: 'ETC Usage Documents', ja: 'ETCカードの利用状況資料',
                bodyEn: 'Organized PDFs with date, route, IC entry/exit, plate match, and amount per line.',
                bodyJa: 'ETCカードを利用した日付・ルート・入口/出口IC・車両のナンバープレート照合・通行料金を1行ずつ整理したPDF。',
              },
              {
                slug: 'authorized-vehicle-registry', Icon: ShieldCheck, color: '#9200C7',
                en: 'Registered Vehicle Checks', ja: '登録車両チェック',
                bodyEn: 'Create a list of vehicles registered with JTR. JTR detects ETC card usage by vehicles not on the list and notifies you.',
                bodyJa: 'JTRに登録した車両リストを作成。リストにない車両でのETCカード利用を検出し、お客様に通知します。',
              },
              {
                slug: 'meisai-connection-health', Icon: Workflow, color: '#0066CC',
                en: 'ETC Usage Inquiry Service Automation', ja: 'ETC利用照会サービスからの情報を自動取得',
                bodyEn: 'JTR retrieves your ETC usage from the ETC Usage Inquiry Service, organizes it, and delivers it to you on a schedule.',
                bodyJa: 'JTRがETCカードの利用状況をETC利用照会サービスから取得・整理し、お客様に配信します。',
              },
              {
                slug: 'ai-pattern-review', Icon: Eye, color: '#5b0080',
                en: 'Vehicle-Usage Visibility', ja: '有料道路利用の可視化',
                bodyEn: 'Our review tools surface unusual usage patterns from your ETC card data and notify you. Humans always make the final decision.',
                bodyJa: '高度なチェック機能がETCカードの利用情報から通常と異なる利用を検出し、お客様に通知します。最終的な判断は人が行います。',
              },
            ].map((f) => {
              const Icon = f.Icon;
              return (
                <Link
                  key={f.slug}
                  to={`/features/${f.slug}`}
                  className="group relative rounded-2xl bg-white border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all"
                  data-testid={`home-feature-${f.slug}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-10 h-10 rounded-lg mb-3 transition-colors"
                    style={{ backgroundColor: `${f.color}15`, color: f.color }}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="text-base font-black text-slate-900 leading-snug mb-1.5">
                    {ja ? f.ja : f.en}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {ja ? f.bodyJa : f.bodyEn}
                  </p>
                  <span className="absolute top-5 right-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all">
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-black tracking-wide transition-colors"
              data-testid="home-features-see-all"
            >
              {ja ? 'すべての機能を見る' : 'See all features'}
              <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* Track-discovery section — Three tracks (Personal · Business ·
          Government & Public-Sector). Iter 108 fix: was previously "Two tracks"
          which silently omitted the Government & Public-Sector path. */}
      <section className="py-12 sm:py-20 bg-gray-50" data-testid="track-discovery-section" data-reveal="focus">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-2">
              {ja ? 'どれが自分に合う？' : 'Which one fits you?'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
              {ja ? '3つのコースから選べます。' : 'Three tracks. Pick the one that fits.'}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link
              to="/personal"
              className="group block rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-7 sm:p-8 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              data-testid="track-personal-card"
            >
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-black uppercase tracking-[0.18em] mb-3">
                <Users className="w-3 h-3" strokeWidth={2.5} />
                {ja ? '個人ドライバー' : 'Individual driver'}
              </div>
              <h3 className="text-2xl font-black tracking-tight leading-tight">
                {ja ? '個人のお客様' : 'For Individuals'}
              </h3>
              <p className="text-emerald-100/85 text-sm mt-2 leading-relaxed">
                {ja
                  ? '最大10枚のETCカードをまとめて管理。日次・週次・月次レポートを自動でお届けします。'
                  : 'Up to 10 ETC cards. Daily, weekly, and monthly reports delivered automatically.'}
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-black group-hover:gap-2.5 transition-all">
                {ja ? '詳しく見る' : 'Explore Personal'}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </div>
            </Link>
            <Link
              to="/business"
              className="group block rounded-3xl bg-gradient-to-br from-[#9200C7] to-[#5b007a] text-white p-7 sm:p-8 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              data-testid="track-business-card"
            >
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-black uppercase tracking-[0.18em] mb-3">
                <Building className="w-3 h-3" strokeWidth={2.5} />
                {ja ? '法人・車両管理担当者' : 'Companies & vehicle managers'}
              </div>
              <h3 className="text-2xl font-black tracking-tight leading-tight">
                {ja ? '法人のお客様' : 'For Business'}
              </h3>
              <p className="text-purple-100/85 text-sm mt-2 leading-relaxed">
                {ja
                  ? '複数のETCカード・車両のETC利用状況をまとめて管理。統合レポートとスマート支出アラートを標準装備。'
                  : 'Manage ETC usage across multiple ETC cards and vehicles. Consolidated reports and Smart Spend Alerts included.'}
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-black group-hover:gap-2.5 transition-all">
                {ja ? '法人向けプランを見る' : 'Explore Business'}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </div>
            </Link>
            <Link
              to="/government"
              className="group block rounded-3xl bg-gradient-to-br from-[#0a1f3d] via-[#04244a] to-[#0066CC] text-white p-7 sm:p-8 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              data-testid="track-government-card"
            >
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-black uppercase tracking-[0.18em] mb-3">
                <ShieldCheck className="w-3 h-3" strokeWidth={2.5} />
                {ja ? '官公庁・公共部門関係' : 'Government & Public-Sector'}
              </div>
              <h3 className="text-2xl font-black tracking-tight leading-tight">
                {ja ? '官公庁・公共部門関係者向け' : 'For Government & Public-Sector'}
              </h3>
              <p className="text-sky-100/85 text-sm mt-2 leading-relaxed">
                {ja
                  ? 'ナンバー種別（一般／事業用／軽／レンタル／公用車）への対応、確認業務に配慮した資料一式、レビューログ。'
                  : 'Plate-family awareness (ordinary / commercial / kei / official), Organized for customer review report bundles, record handling history log.'}
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-black group-hover:gap-2.5 transition-all">
                {ja ? '官公庁・公共部門関係向けプランを見る' : 'Explore Gov & Public-Sector'}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </div>
            </Link>
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">
            {ja
              ? '※ 登録フォームは共通です。フォーム内で「個人」「法人」「官公庁・公共部門関係」のいずれかをお選びください。'
              : 'One sign-up form for all three — you pick the track inside the form.'}
          </p>
        </div>
      </section>

      {/* Two Setup Paths */}
      <section className="py-12 sm:py-20 bg-white" data-testid="paths-section" data-reveal="focus">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0083D3]">{t.pathsTitle}</h2>
            <p className="mt-3 text-base text-gray-500">{t.pathsSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link to="/register" className="block rounded-3xl border-2 border-gray-200 p-8 hover:border-[#9200C7] hover:shadow-xl transition-all group">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mb-5 group-hover:bg-purple-200 transition-colors">
                <CreditCard className="w-8 h-8 text-[#9200C7]" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9200C7]">{t.pathANote}</span>
              <h3 className="mt-2 text-xl font-bold text-gray-900">{t.pathA}</h3>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{t.pathADesc}</p>
              <div className="mt-5 inline-flex items-center text-sm font-bold text-[#9200C7]">
                {lang === 'ja' ? '始める' : 'Get Started'} <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
            <Link to="/register" className="block rounded-3xl border-2 border-gray-200 p-8 hover:border-[#62BB46] hover:shadow-xl transition-all group">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mb-5 group-hover:bg-green-200 transition-colors">
                <Link2 className="w-8 h-8 text-[#62BB46]" strokeWidth={1.5} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#62BB46]">{t.pathBNote}</span>
              <h3 className="mt-2 text-xl font-bold text-gray-900">{t.pathB}</h3>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{t.pathBDesc}</p>
              <div className="mt-5 inline-flex items-center text-sm font-bold text-[#62BB46]">
                {lang === 'ja' ? '接続する' : 'Connect'} <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-20 bg-gray-50" data-testid="why-section" data-reveal="focus">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#9200C7]">{t.whyTitle}</h2>
            <p className="mt-3 text-base text-gray-500">{t.whySub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.whyCards.map((card, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 bg-white p-6 hover:shadow-xl hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors" style={{ backgroundColor: card.color + '15' }}>
                  <card.icon className="w-6 h-6" style={{ color: card.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{card.t}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d] p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-7 h-7 text-[#62BB46]" strokeWidth={1.5} />
              <h2 className="text-2xl font-bold text-white">{t.safetyTitle}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {t.safetyItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                  <Check className="w-5 h-5 text-[#62BB46] flex-shrink-0" strokeWidth={2.5} />{item}
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Link to={`${prefix}/security`}>
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 hover:text-white rounded-full px-6 py-2.5 h-auto text-sm font-semibold" data-testid="safety-link">
                  {lang === 'ja' ? 'セキュリティについて' : 'Learn About Security'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Business */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-[#FF6B00]">{t.bizTitle}</h2>
              <p className="mt-3 text-base text-gray-500">{t.bizDesc}</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {t.bizUsers.map((u, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-[#FF6B00] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <div>
                      <span className="font-medium">{typeof u === 'string' ? u : u.t}</span>
                      {typeof u !== 'string' && u.d && <span className="block text-xs text-gray-400">{u.d}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <Link to={`${prefix}/business`}>
                <Button variant="outline" className="mt-6 border-2 border-[#FF6B00] text-[#FF6B00] hover:bg-orange-50 rounded-full px-6 py-2.5 h-auto text-sm font-semibold" data-testid="biz-link">
                  {lang === 'ja' ? '法人向けサービス' : 'View Business Services'} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex-shrink-0 w-full md:w-80">
              <div className="rounded-3xl bg-white border-2 border-orange-200 p-8 shadow-lg">
                <Building className="w-12 h-12 text-[#FF6B00] mb-4" strokeWidth={1} />
                <h3 className="text-xl font-bold text-gray-900">{lang === 'ja' ? 'ETC利用記録を整理' : 'Organized ETC Records'}</h3>
                <p className="mt-2 text-sm text-gray-500">{lang === 'ja' ? '日次・週次・月次のETC利用記録をCSV・PDFで整理。' : 'Daily, weekly & monthly ETC usage records in CSV & PDF.'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-20 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#9200C7] text-center mb-10">{t.faqTitle}</h2>
          <div className="space-y-3">
            {t.faqs.map((faq, i) => (
              <details key={i} className="group rounded-2xl border-2 border-gray-200 bg-white overflow-hidden hover:border-purple-300 transition-colors">
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer text-sm font-semibold text-gray-900 hover:bg-gray-50 transition-colors list-none">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="w-5 h-5 text-[#9200C7] flex-shrink-0" strokeWidth={1.5} />{faq.q}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" strokeWidth={2} />
                </summary>
                <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed ml-8">{faq.a}</div>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center space-y-3">
            <Link to={`${prefix}/faq`} className="text-sm font-bold text-[#9200C7] hover:underline" data-testid="faq-view-all">
              {lang === 'ja' ? 'FAQをすべて見る' : 'View All FAQs'} <ArrowRight className="inline w-4 h-4 ml-1" />
            </Link>
            <div>
              <Link to={`${prefix}/${lang === 'ja' ? 'etc-ryoshusho-riyoumeisai-guide' : 'japan-etc-receipt-guide'}`} className="text-sm font-bold text-[#9200C7] hover:underline" data-testid="home-pillar-link">
                {lang === 'ja' ? 'ETC領収書・利用明細ガイドを読む' : 'Read the Japan ETC Receipt & Usage Guide'} <ArrowRight className="inline w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQPage structured data — matches the visible FAQ above. Unlocks
          People-Also-Ask / FAQ rich-result / AI-Overview eligibility for the
          homepage's head terms (ETC 領収書 / Japan ETC receipt). Localized via `t`. */}
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        inLanguage: lang === 'ja' ? 'ja-JP' : 'en-US',
        mainEntity: t.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      }} />

      {/* Testimonials — LIVE federated feed (2,597+ real reviews) */}
      <section className="py-12 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-6">
            <NBCOlympicsBadge />
          </div>
        </div>
        <Suspense fallback={<div className="min-h-[200px]" aria-hidden="true" />}>
          <RealTestimonialsStrip accent="#9200C7" />
        </Suspense>
      </section>

      {/* Built from real ETC customer problems — Our Story trust card.
          Sits between the testimonials and "Trusted Since 2013" sections
          so visitors who scrolled past the hero see another, deeper
          reason to trust JTR before the proof-points wall hits. */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-violet-50/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-violet-200 rounded-3xl shadow-sm p-7 sm:p-10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <BookOpen className="w-4 h-4 text-violet-700" strokeWidth={2.2} />
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-violet-700">
                {ja ? '私たちのこと' : 'Our Story'}
              </p>
            </div>
            <h2 className={`text-2xl sm:text-3xl font-black text-slate-900 leading-tight mb-4 ${ja ? 'jtr-ja-text' : ''}`}>
              {ja ? 'JapanETCcard.comのお客様の声から生まれました' : 'Built from real ETC customer problems'}
            </h2>
            <p className={`text-base text-slate-600 leading-relaxed mb-6 ${ja ? 'jtr-ja-text' : ''}`}>
              {ja
                ? '日本全国のお客様にご利用いただいています'
                : 'Trusted by customers across Japan'}
            </p>
            <Link
              to={`/${lang}/founders-message`}
              className="inline-flex items-center gap-1.5 px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
              data-testid="homepage-trust-our-story-cta"
            >
              {ja ? 'JTRについて' : 'Read Our Story'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted Since 2013 */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#9200C7] text-center mb-4">
            {ja ? '実績ある運営会社が提供' : 'Backed by Proven ETC Experience'}
          </h2>
          <p className="text-base text-gray-500 text-center mb-12 max-w-2xl mx-auto">
            {ja ? '10年以上のETC関連サービス実績を持つ企業が運営' : 'Operated by a company with over a decade of ETC service experience in Japan'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Star, color: '#9200C7', title: ja ? '長年のETC事業実績' : 'Proven ETC Experience', desc: ja ? '運営会社のNoJo Enterpriseは2013年から日本でETC関連サービスを提供してきました。Japan Toll Receiptsはその実績をもとにした新しいサービスで、英語対応も万全です。' : 'NoJo Enterprise has provided ETC-related services in Japan since 2013, trusted by drivers nationwide. Japan Toll Receipts builds on that experience, with full English support included.' },
              { icon: Users, color: '#0083D3', title: ja ? 'きめ細やかなサポート' : 'Personal Service', desc: ja ? '自動応答ではなく、専任スタッフが直接対応します。日本語・英語の両方でサポートします。' : 'Direct support from real people who understand your needs — not automated systems. Our team speaks both Japanese and English.' },
              { icon: Shield, color: '#62BB46', title: ja ? 'コンプライアンスに配慮した設計' : 'Full Compliance', desc: ja ? '関連法規や規則に配慮し、安心してご利用いただける運用を目指しています。' : 'Properly operated in compliance with all relevant regulations for your peace of mind.' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: item.color + '15' }}>
                  <item.icon className="w-8 h-8" style={{ color: item.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0083D3] text-center mb-4">
            {ja ? '対象のお客様' : 'Who We Serve'}
          </h2>
          <p className="text-base text-gray-500 text-center mb-12">
            {ja ? '日本全国のETC利用者様へ' : 'ETC toll receipt reports for every community in Japan'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Building, color: '#9200C7', title: ja ? '法人様' : 'Japanese Companies', desc: ja ? '社用車の経費管理、月次レポート作成、部門別集計などにご活用いただけます。' : 'Corporate vehicle expense management, monthly reports, and department analysis.' },
              { icon: Users, color: '#0083D3', title: ja ? 'SOFA・MOFA・MOC関係者の皆様' : 'U.S. Military Families', desc: ja ? 'SOFA、MOFA、MOCステータスの方に対応。英語サポートもご用意しています。' : 'SOFA, MOFA, MOC status personnel at every base, with English support.' },
              { icon: Globe, color: '#62BB46', title: ja ? '外国人居住者の方' : 'Foreign Residents', desc: ja ? '日本在住の多くの外国人ドライバーの方へ。日本語が不安な方にも、日本語・英語でサポートします。' : 'All foreign residents in Japan. No Japanese required, fully bilingual.' },
              { icon: Truck, color: '#FF6B00', title: ja ? '配送・運送業者' : 'Delivery & Logistics', desc: ja ? '配送ルートの通行料金を日次レポートで把握、効率的に管理。' : 'Track delivery route tolls with daily reports for efficient management.' },
              { icon: Receipt, color: '#9200C7', title: ja ? '個人事業主' : 'Sole Proprietors', desc: ja ? '記録整理や経費管理に役立つETC利用記録を整理してお届け。' : 'Organized ETC records for record keeping and expense management.' },
              { icon: Calendar, color: '#0083D3', title: ja ? '出張が多い方' : 'Business Travelers', desc: ja ? '出張経費精算に必要な通行記録を自動で取得。' : 'Automatically capture accurate toll records for travel expense claims.' },
            ].map((item, i) => (
              <div key={i} className="rounded-2xl bg-white border border-gray-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: item.color + '15' }}>
                  <item.icon className="w-6 h-6" style={{ color: item.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibility */}
      <section className="bg-gray-50 py-4 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] text-gray-400 leading-relaxed text-center">
            {t.responsibility.map((line, i) => <span key={i} className="block">{line}</span>)}
          </p>
        </div>
      </section>

      {/* Built for Japan ETC workflows — strategic positioning */}
      <HomeWorkflowSection ja={ja} />

      {/* Early Access waitlist CTA — pre-launch email capture (single bottom CTA) */}
      <section className="bg-[#0f0a1f] py-16" data-testid="home-early-access-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <EarlyAccessCTA />
        </div>
      </section>

      <Footer />
    </div>
  );
}
